import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { Action } from 'state/actions';
import type { DateFilter } from 'state/blotters/common/blotter.model';
import { toLocaleDate, toLocaleDateOnlyString } from 'state/blotters/common/blotter.utils';
import { dateNow } from 'utils/core/now';

import {
  onColumnSortChanged,
  onColumnStateChanged,
  onItemsPerPageChanged,
  onPageChanged,
} from '../common/blotter.reducer.utils';
import type {
  QuoteBlotterColumnDefinitionReceived,
  QuoteBlotterFilterChanged,
  QuoteBlotterFiltersLoaded,
  QuoteBlotterPreferenceStateLoaded,
} from './quoteBlotter.action';
import type { QuoteBlotterState } from './quoteBlotter.model';
import { initialQuoteBlotterState } from './quoteBlotter.model';

// tslint:disable-next-line:cyclomatic-complexity
export function quoteBlotterReducer(
  state: QuoteBlotterState = initialQuoteBlotterState,
  action: Action,
): QuoteBlotterState {
  switch (action.type) {
    case 'QUOTE_BLOTTER_COLUMN_DEFINITION_RECEIVED':
      return onColumnDefinitionReceived(state, action);
    case 'QUOTE_BLOTTER_FILTERS_LOADED':
      return onFiltersLoaded(state, action);
    case 'QUOTE_BLOTTER_FILTERS_LOADING_FAILED':
      return onFiltersLoadingFailed(state);
    case 'QUOTE_BLOTTER_FILTER_CHANGED':
      return onFilterChanged(state, action);
    case 'QUOTE_BLOTTER_ADVANCED_FILTERS_TOGGLED':
      return onAdvancedFilterToggled(state);
    case 'QUOTE_BLOTTER_FILTERS_PERSISTENCE_FAILED':
      return { ...state };
    case 'QUOTE_BLOTTER_PAGE_CHANGED':
      return onPageChanged(state, action);
    case 'QUOTE_BLOTTER_ITEMS_PER_PAGE_CHANGED':
      return onItemsPerPageChanged(state, action);
    case 'QUOTE_BLOTTER_PREFERENCE_STATE_GET':
      return { ...state, areBlotterPreferencesInError: false };
    case 'QUOTE_BLOTTER_PREFERENCE_STATE_LOADED':
      return onBlotterPreferenceStateLoaded(state, action);
    case 'QUOTE_BLOTTER_COLUMN_STATE_CHANGED':
      return onColumnStateChanged(state, action);
    case 'QUOTE_BLOTTER_SORT_CHANGED':
      return onColumnSortChanged(state, action);
    case 'QUOTE_BLOTTER_FETCHING_FAILED':
      return onQuoteBlotterFetchingFailed(state);
    case 'QUOTE_BLOTTER_PREFERENCE_STATE_LOADING_FAILED':
      return onQuoteBlotterPreferenceStateLoadingFailed(state, action.defaultPref);
    case 'QUOTE_BLOTTER_PREFERENCE_STATE_PERSISTENCE_FAILED':
      return onQuoteBlotterPreferenceStatePersistenceFailed(state);
    case 'QUOTE_BLOTTER_PLATFORMS_FETCHING':
      return { ...state, availablePlatformsFetchingStatus: 'loading' };
    case 'QUOTE_BLOTTER_PLATFORMS_FETCHED':
      return { ...state, availablePlatforms: action.data, availablePlatformsFetchingStatus: 'success' };
    case 'QUOTE_BLOTTER_PLATFORMS_FETCH_FAILED':
      return { ...state, availablePlatformsFetchingStatus: 'error' };
    case 'QUOTE_BLOTTER_SET_LOADING':
      return { ...state, loading: action.isLoading, isFetchingFailed: false };
    default:
      return state;
  }
}

function onColumnDefinitionReceived(
  state: QuoteBlotterState,
  { columnDefinition }: QuoteBlotterColumnDefinitionReceived,
): QuoteBlotterState {
  return {
    ...state,
    columnDefinition,
    sort: [],
  };
}

function onFiltersLoaded(state: QuoteBlotterState, { filters }: QuoteBlotterFiltersLoaded): QuoteBlotterState {
  return { ...state, filters };
}

function onFiltersLoadingFailed(state: QuoteBlotterState): QuoteBlotterState {
  return { ...state, isFiltersLoadingFailed: true };
}

function onFilterChanged(state: QuoteBlotterState, { filter, value }: QuoteBlotterFilterChanged): QuoteBlotterState {
  switch (filter) {
    case 'dateFilter': {
      const dateFilter = <DateFilter>value;
      return {
        ...state,
        filters: { ...state.filters, dateFilter, startDate: undefined, endDate: undefined },
        page: 1,
      };
    }
    case 'startDate': {
      const startDate = value as string;
      const endDate = state.filters.endDate || toLocaleDateOnlyString(new Date(dateNow())); // yyyy-MM-dd

      return { ...state, filters: { ...state.filters, startDate, endDate }, page: 1 };
    }
    case 'endDate': {
      const endDate = value as string;
      const startDate = state.filters.startDate || toLocaleDateOnlyString(toLocaleDate(state.filters.dateFilter)); // yyyy-MM-dd

      return { ...state, filters: { ...state.filters, startDate, endDate }, page: 1 };
    }
    default:
      return { ...state, filters: { ...state.filters, [filter]: value }, page: 1 };
  }
}

function onAdvancedFilterToggled(state: QuoteBlotterState): QuoteBlotterState {
  const filters = { ...state.filters, advancedFilters: !state.filters.advancedFilters };
  const result = { ...state, filters, page: 1 } satisfies QuoteBlotterState;
  if (filters.advancedFilters === false) {
    return { ...result, filters: { ...filters, currencyFilter: 'allCurrencies' } };
  }
  return result;
}

function onBlotterPreferenceStateLoaded(
  state: QuoteBlotterState,
  { blotterPreferenceState }: QuoteBlotterPreferenceStateLoaded,
): QuoteBlotterState {
  return {
    ...state,
    areBlotterPreferencesInError: false,
    blotterPreferenceState,
    canSaveBlotterPreferences: true,
    loading: false,
  };
}

function onQuoteBlotterFetchingFailed(state: QuoteBlotterState): QuoteBlotterState {
  return { ...state, isFetchingFailed: true, loading: false };
}

function onQuoteBlotterPreferenceStateLoadingFailed(
  state: QuoteBlotterState,
  defaultPref: IBlotterPreferenceState,
): QuoteBlotterState {
  return {
    ...state,
    areBlotterPreferencesInError: true,
    canSaveBlotterPreferences: false,
    loading: false,
    blotterPreferenceState: defaultPref,
  };
}

function onQuoteBlotterPreferenceStatePersistenceFailed(state: QuoteBlotterState): QuoteBlotterState {
  return { ...state, isQuoteBlotterPreferenceStatePersistenceFailed: false, loading: false };
}
